import {
  DEFAULT_LOCALE,
  getLocaleDirection,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '@/i18n/locales'

const SUPPORTED = new Set<string>(SUPPORTED_LOCALES)
const RTL_SUPPORTED = SUPPORTED_LOCALES.filter((locale) => getLocaleDirection(locale) === 'rtl')
const NEXT_LOCALE_COOKIE_PATTERN = /(?:^|; )NEXT_LOCALE=([^;]*)/

export function normalizeSupportedLocale(value: string | null | undefined): SupportedLocale | null {
  const [language] = (value ?? '').toLowerCase().split('-')
  return SUPPORTED.has(language) ? (language as SupportedLocale) : null
}

// Client-side mirror of src/i18n/request.ts: NEXT_LOCALE cookie, then browser
// languages, then the default locale.
export function resolveClientLocale(): SupportedLocale {
  if (typeof document === 'undefined') {
    return DEFAULT_LOCALE
  }
  const cookieMatch = document.cookie.match(NEXT_LOCALE_COOKIE_PATTERN)
  const fromCookie = normalizeSupportedLocale(
    cookieMatch ? decodeURIComponent(cookieMatch[1]) : null
  )
  if (fromCookie) {
    return fromCookie
  }
  for (const candidate of navigator.languages ?? [navigator.language]) {
    const supported = normalizeSupportedLocale(candidate)
    if (supported) {
      return supported
    }
  }
  return DEFAULT_LOCALE
}

// Same resolution as resolveClientLocale, inlined in <head> so <html lang/dir>
// is correct before first paint while the shell stays statically prerenderable.
export const LOCALE_INIT_SCRIPT = `(function(){try{var s=${JSON.stringify([
  ...SUPPORTED_LOCALES,
])},r=${JSON.stringify(RTL_SUPPORTED)};function n(v){v=(v||"").toLowerCase().split("-")[0];return s.indexOf(v)>=0?v:null}var m=document.cookie.match(/(?:^|; )NEXT_LOCALE=([^;]*)/),l=n(m&&decodeURIComponent(m[1]));if(!l){var b=navigator.languages||[navigator.language];for(var i=0;i<b.length&&!l;i++)l=n(b[i])}l=l||${JSON.stringify(
  DEFAULT_LOCALE
)};var d=document.documentElement;d.lang=l;d.dir=r.indexOf(l)>=0?"rtl":"ltr"}catch(e){}})()`

type Listener = () => void
const listeners = new Set<Listener>()

export function subscribeToClientLocale(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getClientLocale(): SupportedLocale {
  if (typeof document === 'undefined') {
    return DEFAULT_LOCALE
  }
  return normalizeSupportedLocale(document.documentElement.lang) ?? DEFAULT_LOCALE
}

// The static shell never re-renders <html lang/dir> from the server, so locale
// changes apply to the live document here and notify the root intl provider.
export function applyClientLocale(locale: string): void {
  if (typeof document === 'undefined') {
    return
  }
  const supported = normalizeSupportedLocale(locale)
  if (!supported) {
    return
  }
  const root = document.documentElement
  root.lang = supported
  root.dir = getLocaleDirection(supported)
  for (const listener of listeners) {
    listener()
  }
}
